import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, TextField, Typography,
} from '@mui/material';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import api from '../services/api';
import { StatusChip } from '../quotas/QuotaTable';
import { SYSTEM_LABELS } from '../quotas/trackerSeries';
import { FooterActions, Panel, PanelAlerts, PanelHeader, PanelSection } from '../wrangler/PanelLayout';
import { CopyField, partnerName, shareLink, when } from './common';
import { PartnerDialog, PasswordHelp } from './PartnerDialogs';

const errorText = (err, fallback) => err?.response?.data?.error || fallback;

/**
 * Sharing one quota view (Stack only, 7k): its link (copy, regenerate, expiry, last opened) and
 * the partner accounts that see it in their list. Every change is saved at once.
 */
const SharingPanel = ({ monitor, onClose }) => {
  const [sharing, setSharing] = useState(null);
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState(null);
  const [pending, setPending] = useState(false);
  const [dialog, setDialog] = useState(null); // 'regenerate' | 'new-partner'
  const [help, setHelp] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/quota-monitors/${monitor.id}/sharing/`), api.get('/auth/partners/')])
      .then(([s, p]) => { setSharing(s.data); setPartners(p.data); })
      .catch(() => setError("Sharing can't be loaded just now."));
  }, [monitor.id]);

  const byId = useMemo(() => new Map(partners.map((p) => [p.id, p])), [partners]);
  const shared = (sharing?.partner_ids || []).map((id) => byId.get(id)).filter(Boolean);
  const addable = partners.filter((p) => p.is_active && !sharing?.partner_ids.includes(p.id));

  const change = async (request) => {
    setPending(true);
    setError(null);
    try {
      const { data } = await request();
      setSharing(data);
      return data;
    } catch (err) {
      setError(errorText(err, "That change couldn't be saved."));
      return null;
    } finally {
      setPending(false);
    }
  };
  const patch = (body) => change(() => api.patch(`/quota-monitors/${monitor.id}/sharing/`, body));
  const setPartnerIds = (ids) => patch({ partner_ids: ids });
  const regenerate = async () => {
    setDialog(null);
    await change(() => api.post(`/quota-monitors/${monitor.id}/sharing/regenerate/`));
  };

  const systemLabel = SYSTEM_LABELS[monitor.partner_system] || monitor.partner_system;
  const footer = <FooterActions primary={<Button variant="contained" onClick={onClose} sx={{ textTransform: 'none' }}>Done</Button>} />;

  return (
    <Panel busy={pending} onClose={onClose} footer={footer}>
      <PanelHeader title={`Share ${monitor.quota_view_name}`} onClose={onClose} busy={pending}
        chips={sharing && (sharing.active ? <StatusChip label="Link active" colour="success" /> : <StatusChip label="Link expired" colour="error" />)} />
      <PanelAlerts>
        <Typography variant="body2" color="text.secondary">
          Partners see this view&apos;s {systemLabel} quotas only, with name, description, completes, limit and progress,
          and nothing that tells them there are two systems. Change which in Quota Monitoring.
        </Typography>
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      </PanelAlerts>

      {!sharing && !error && <Box sx={{ px: 3 }}><CircularProgress size={24} /></Box>}
      {sharing && (
        <>
          <PanelSection title="Link">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Anyone with this link sees this view, read-only, without signing in. Send it only to the people who should see it.
            </Typography>
            <CopyField label="Link" value={shareLink(sharing.token)} />
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mt: 2, mb: 2 }}>
              <Typography variant="body2" color="text.secondary">Made {when(sharing.token_created_at)}</Typography>
              <Typography variant="body2" color="text.secondary">Last opened: {when(sharing.last_opened_at)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size="small" type="date" label="Works until" InputLabelProps={{ shrink: true }}
                value={sharing.expires_on || ''} disabled={pending}
                onChange={(e) => patch({ expires_on: e.target.value || null })}
                helperText={sharing.expires_on ? 'The last day the link works.' : 'No expiry date: it works until regenerated.'}
                sx={{ width: 220 }} />
              {sharing.expires_on && (
                <Button size="small" onClick={() => patch({ expires_on: null })} disabled={pending} sx={{ textTransform: 'none', mb: 2.5 }}>
                  Remove expiry
                </Button>
              )}
              <Box sx={{ flex: 1 }} />
              <Button color="error" variant="outlined" onClick={() => setDialog('regenerate')} disabled={pending}
                sx={{ textTransform: 'none', mb: 2.5 }}>
                Regenerate link…
              </Button>
            </Box>
          </PanelSection>

          <PanelSection title="Partner accounts">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              These accounts see this view in their list after signing in.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              {shared.length === 0 && <Typography variant="body2" color="text.secondary">None yet.</Typography>}
              {shared.map((p) => (
                <Chip key={p.id} label={`${partnerName(p)}${p.company ? ` · ${p.company}` : ''}${p.is_active ? '' : ' (deactivated)'}`}
                  onDelete={pending ? undefined : () => setPartnerIds(sharing.partner_ids.filter((id) => id !== p.id))} />
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <Autocomplete
                options={addable}
                getOptionLabel={(p) => `${partnerName(p)}${p.company ? ` · ${p.company}` : ''} (${p.username})`}
                value={null}
                blurOnSelect
                disabled={pending}
                onChange={(e, p) => p && setPartnerIds([...sharing.partner_ids, p.id])}
                renderInput={(params) => <TextField {...params} size="small" label="Add a partner account" />}
                sx={{ flex: 1, minWidth: 260 }}
              />
              <Button startIcon={<PersonAddRoundedIcon />} onClick={() => setDialog('new-partner')} disabled={pending}
                sx={{ textTransform: 'none' }}>
                New partner account…
              </Button>
            </Box>
          </PanelSection>
        </>
      )}

      <Dialog open={dialog === 'regenerate'} onClose={() => setDialog(null)}>
        <DialogTitle>Regenerate the link?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            The current link stops working at once: anyone using it will see &quot;This link is no longer active&quot;
            until you send them the new one. Partner accounts aren&apos;t affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button color="error" variant="contained" onClick={regenerate} sx={{ textTransform: 'none' }}>Regenerate</Button>
        </DialogActions>
      </Dialog>
      {dialog === 'new-partner' && (
        <PartnerDialog onClose={() => setDialog(null)} onSaved={async (partner, passwordHelp) => {
          setDialog(null);
          setPartners((list) => [partner, ...list]);
          await setPartnerIds([...sharing.partner_ids, partner.id]);
          setHelp({ partner, help: passwordHelp });
        }} />
      )}
      {help && <PasswordHelp help={help.help} partner={help.partner} onClose={() => setHelp(null)} />}
    </Panel>
  );
};

export default SharingPanel;
